/**
 * Lernen S1/S2 (Antoine, 27.09.2026, Entscheidungen 4 und 5) — dezent im Builder:
 *   - „Orientiert an: [Fall A, Fall B] – dort hast du …“, je Fall ein Link zum Vorgang. Die Daten
 *     liefert der Server in `offer_options.ai_meta.orientiertAn` (lib/lernen.ts#baueOrientierung);
 *     hier wird nur angezeigt, nichts entschieden.
 *   - der Schalter „Nicht zum Lernen verwenden“ (PUT /api/events/:id/lernen).
 *   - Lernen S4: „Angewendet: R3, R5“ — nur Schlüsse, deren Änderung der Server im Entwurf gefunden
 *     hat (`ai_meta.angewendet`), und die Rundungsregel der Preis-Engine (`ai_meta.rundung`).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { apiMutate } from "../hooks/useApi";
import { Icon, Toggle } from "./components";
import type { AngewendeterSchluss, Orientierung, RundungsVermerk } from "./types";

const euro = (c: number) => `${(c / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const SCHRITT: Record<number, string> = { 100: "volle Euro", 500: "volle 5 €", 1000: "volle 10 €" };

export function LernHinweis({
  eventId,
  orientiertAn,
  angewendet,
  rundung,
  ausgeschlossen,
  onGeaendert,
  schalterAusblenden = false,
}: {
  eventId: string;
  orientiertAn: Orientierung[] | undefined;
  angewendet?: AngewendeterSchluss[];
  rundung?: RundungsVermerk;
  /** null = Schalter noch nicht verfuegbar (Migration 544 fehlt) -> nicht anzeigen. */
  ausgeschlossen: boolean | null;
  onGeaendert: () => void;
  /** Mockup v3 (Builder): der Schalter steht dort im „⋯"-Menü (`LernSchalter`), nicht hier. */
  schalterAusblenden?: boolean;
}) {
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  async function schalten(wert: boolean) {
    setSpeichert(true);
    setFehler(null);
    try {
      await apiMutate(`/api/events/${eventId}/lernen`, "PUT", { ausgeschlossen: wert });
      onGeaendert();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setSpeichert(false);
    }
  }
  if (!orientiertAn?.length && !angewendet?.length && !rundung && (ausgeschlossen === null || schalterAusblenden)) return null;
  return (
    <div
      className="flex flex-col gap-2 rounded-[14px] border border-outline-variant/60 bg-surface-container-lowest px-4 py-3 font-body-sm text-body-sm text-on-surface-variant dark:border-white/10 dark:bg-white/5"
      data-testid="lern-hinweis"
    >
      {orientiertAn?.length ? (
        <div className="flex items-start gap-2">
          <Icon name="psychology" className="mt-0.5 text-[18px] text-primary" />
          <div className="min-w-0 space-y-1">
            <p className="text-on-surface dark:text-inverse-on-surface">Orientiert an deinen früheren Angeboten:</p>
            <ul className="space-y-1">
              {orientiertAn.map((o) => (
                <li key={o.eventId} className="break-words">
                  <Link to={`/vorgaenge/${o.eventId}`} className="font-medium text-primary underline-offset-2 hover:underline">
                    {o.titel}
                  </Link>
                  {" – "}
                  {o.uebernommen > 0
                    ? `${o.uebernommen} der ${o.vonGesendet} Positionen, die du dort gesendet hast, stehen auch hier`
                    : "ähnlicher Anlass und Rahmen"}
                  {o.betreiberGeaendert === true ? " (dort hattest du den KI-Vorschlag angepasst)" : o.betreiberGeaendert === false ? " (dort ging der KI-Vorschlag unverändert raus)" : ""}
                  .
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
      {angewendet?.length ? (
        <div className="flex items-start gap-2" data-testid="lern-angewendet">
          <Icon name="rule" className="mt-0.5 text-[18px] text-primary" />
          <div className="min-w-0 space-y-1">
            <p className="text-on-surface dark:text-inverse-on-surface">
              Angewendet: {angewendet.length === 1 ? "1 deiner Regeln" : `${angewendet.length} deiner Regeln`}
            </p>
            <ul className="space-y-1">
              {angewendet.map((a) => (
                <li key={a.id} className="flex min-w-0 items-baseline gap-2">
                  <span className="shrink-0 rounded-md bg-primary-container px-1.5 py-0.5 font-label-md text-[11px] font-semibold text-on-primary-container">R{a.nummer}</span>
                  <Link to="/gedaechtnis?tab=so-lerne-ich" className="min-w-0 break-words hover:text-primary">{a.text}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
      {rundung ? (
        <div className="flex items-start gap-2" data-testid="lern-rundung">
          <Icon name="tune" className="mt-0.5 text-[18px] text-primary" />
          <p className="min-w-0 break-words">
            Menüpreis pro Gast {rundung.richtung === "auf" ? "aufgerundet" : "abgerundet"} auf {SCHRITT[rundung.schrittCents] ?? euro(rundung.schrittCents)}: {euro(rundung.vorherProGastCents)} → {euro(rundung.nachherProGastCents)}.
            <span className="block text-[12px] text-on-surface-variant/80">Deine Rundungsregel aus den Preiseinstellungen — gerechnet von der Preis-Engine, nicht von der KI.</span>
          </p>
        </div>
      ) : null}
      {ausgeschlossen === null || schalterAusblenden ? null : (
      <div className="flex items-center justify-between gap-3">
        <span className="min-w-0">
          Nicht zum Lernen verwenden
          <span className="block text-[12px] text-on-surface-variant/80">Dieses Angebot dient MAESTRO dann nie als Beispiel.</span>
        </span>
        <Toggle checked={ausgeschlossen} onChange={schalten} disabled={speichert} label="Nicht zum Lernen verwenden" />
      </div>
      )}
      {fehler ? <p className="text-[12px] text-error">{fehler}</p> : null}
    </div>
  );
}

/**
 * Mockup v3: derselbe Schalter „Nicht zum Lernen verwenden" als Menüzeile (Builder-„⋯"-Menü) —
 * dieselbe Route (`PUT /api/events/:id/lernen`), dieselbe Beschriftung.
 */
export function LernSchalter({ eventId, ausgeschlossen, onGeaendert }: { eventId: string; ausgeschlossen: boolean; onGeaendert: () => void }) {
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  async function schalten(wert: boolean) {
    setSpeichert(true);
    setFehler(null);
    try {
      await apiMutate(`/api/events/${eventId}/lernen`, "PUT", { ausgeschlossen: wert });
      onGeaendert();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setSpeichert(false);
    }
  }
  return (
    <div className="px-3.5 py-2 font-body-sm text-[13px] text-on-background dark:text-inverse-on-surface" data-testid="lern-schalter">
      <div className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-start gap-2.5">
          <Icon name="psychology" className="text-[17px] text-on-surface-variant/70 dark:text-secondary-fixed-dim" />
          <span className="min-w-0">
            Nicht zum Lernen verwenden
            <span className="block text-[11.5px] text-secondary dark:text-secondary-fixed-dim">Dieses Angebot dient MAESTRO dann nie als Beispiel.</span>
          </span>
        </span>
        <Toggle checked={ausgeschlossen} onChange={schalten} disabled={speichert} label="Nicht zum Lernen verwenden" />
      </div>
      {fehler ? <p className="mt-1 text-[12px] text-error">{fehler}</p> : null}
    </div>
  );
}
